/**
 * PRD.328 — Permanent Patient Deletion, client side.
 *
 * THE CLIENT DELETES EXACTLY ONE ROW.
 *
 * It is tempting to cascade here, the way `delete-encounter-service` does. Phase 1
 * proved that would be wrong and dangerous:
 *
 *  - 21 of the 79 tables in the erasure graph are unreachable from the client — 16
 *    have no syncWrite registry entry, 5 deliberately reject DELETE. A cascade would
 *    silently miss them, so "permanently deleted" would be a lie.
 *  - Worse, an unallowlisted table 403s the whole batch and a rejected DELETE 500s it.
 *    Neither is retryable, so PowerSync would re-offer the same doomed CRUD
 *    transaction forever while local SQLite already showed the patient gone: the
 *    device stops syncing entirely and diverges from the server permanently.
 *
 * So the client emits `DELETE FROM patients WHERE id = ?` and nothing else. syncWrite
 * recognises that as a root lifecycle operation and expands it into the full cascade
 * inside one Postgres transaction, deleting `patients` last. Because the upload
 * contains a single allowlisted, unguarded operation, it either commits or fails
 * cleanly and is retried as a whole.
 *
 * Dependent rows disappear locally when PowerSync receives the server-side deletions.
 * The missing root is authoritative: the workspace closes immediately rather than
 * lingering over a half-propagated graph.
 */

/** The word the operator must type. Deliberately not the patient name (PRD.328 §2). */
export const PERMANENT_DELETE_CONFIRMATION = 'delete';

/** Minimal shape of the PowerSync handle this service needs. */
interface Db {
  execute: (sql: string, params?: unknown[]) => Promise<unknown>;
}

/**
 * Case- and whitespace-insensitive so the operator is not fighting autocapitalise on a
 * tablet, but it still has to be the actual word — no partial or empty match.
 */
export function isPermanentDeleteConfirmed(input: string): boolean {
  return input.trim().toLowerCase() === PERMANENT_DELETE_CONFIRMATION;
}

export interface PermanentDeletePatientParams {
  db: Db;
  patientId: string;
  /** Whatever the operator typed into the confirmation field. */
  confirmation: string;
}

export interface PermanentDeletePatientResult {
  deleted: boolean;
  error?: string;
}

export async function permanentlyDeletePatient(
  params: PermanentDeletePatientParams,
): Promise<PermanentDeletePatientResult> {
  const { db, patientId, confirmation } = params;

  if (!patientId) return { deleted: false, error: 'No patient selected.' };
  if (!isPermanentDeleteConfirmed(confirmation)) {
    return { deleted: false, error: `Type "${PERMANENT_DELETE_CONFIRMATION}" to confirm.` };
  }

  try {
    // The one and only write. `db.execute` (never `writeTransaction` for a single
    // statement) produces the CRUD entry syncWrite turns into the cascade.
    await db.execute('DELETE FROM patients WHERE id = ?', [patientId]);
    return { deleted: true };
  } catch (err) {
    return {
      deleted: false,
      error: err instanceof Error ? err.message : 'Could not delete the patient.',
    };
  }
}
